import React, { Component } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import ValueLink from 'sb-valuelink'
import actions from 'redux/actions'
import storeData from 'sb-store-data'
import { createSelector } from 'reselect'
import { connect } from 'redaction'
import { getUserFullNameFromState, constants, compose, snowplow, mixpanel } from 'common/helpers'
import { FormattedMessage } from 'sb-react-intl'

import injectGlobals from 'decorators/injectGlobals'

import { Box } from 'components/_new/layout'
import Button from 'components/buttons/Button/Button'
import Modal from 'components/modal/Modal/Modal'
import Input from 'components/forms/Input/Input'
import Link from 'components/ui/Link/Link'

import { offerTestReasons, checkIfUnsubscribeOfferTestAvailable } from './util/unsubscribeOfferTest'

import messages from './messages'
import s from './OneLastQuestionModal.scss'

import rachelPhoto from './images/rachel.png'
import sergeyPhoto from './images/sergey.png'
import andreyPhoto from './images/andrey.png'
import mariyaPhoto from './images/mariya.png'


class OneLastQuestionModal extends Component {

  static propTypes = {
    name: PropTypes.string.isRequired,
    userFullName: PropTypes.string,
  }

  static defaultProps = {
    name: constants.modals.cancelSubscription.OneLastQuestion,
  }

  state = {
    activeReasonName: null,
    reasonMessage: '',
    submitted: false,
  }

  componentDidMount() {
    const { isUnsubscribeOfferTestAvailable } = this.props

    if (isUnsubscribeOfferTestAvailable) {
      mixpanel.track(constants.analyticsEvents.cancelOffer25off.start)
    }

    snowplow.track(constants.snowplowEvents.unsubscribeReasonsPopup.open, {}, true)
  }

  handleCancelClick = () => {
    const { activeReasonName, reasonMessage } = this.state
    const { isProlongOfferVisible } = this.props

    this.setState({
      submitted: true,
    })

    // trim to prevent submit with empty multiline string
    if (!reasonMessage.trim()) {
      return
    }

    const unsubscribeData = {
      reason: activeReasonName,
      comment: reasonMessage,
    }

    const isUnsubscribeProductsModalVisible = activeReasonName === 'MANY_PERFUMES'

    if (isUnsubscribeProductsModalVisible) {
      this.trackUnsubscribeReason(activeReasonName, reasonMessage)
      actions.models.modals.open(constants.modals.cancelSubscription.UnsubscribeProducts, unsubscribeData)
      this.close()
    }
    else if (isProlongOfferVisible) {
      this.trackUnsubscribeReason(activeReasonName, reasonMessage)
      actions.models.modals.open(constants.modals.cancelSubscription.ProlongOffer, unsubscribeData)
      this.close()
    }
    else {
      actions.models.subscription.unsubscribe(unsubscribeData)
        .then(({ isRefundAvailable, refund }) => {
          this.trackUnsubscribeReason(activeReasonName, reasonMessage)

          if (isRefundAvailable || refund) {
            actions.models.modals.open(constants.modals.cancelSubscription.Refund, refund)
          }
          else {
            actions.models.modals.open(constants.modals.cancelSubscription.SubscriptionWasCancelled)
          }

          this.close()
        })
    }
  }

  close = () => {
    const { name } = this.props

    actions.models.modals.close(name)
  }

  trackCloseModal = (action, closeType) => {
    snowplow.track(constants.snowplowEvents.cancelSubscriptionStepComplete, {
      stepName: 'unsubscribe reason',
      action,
    }, false)

    if (closeType) {
      snowplow.track(constants.snowplowEvents.unsubscribeReasonsPopup.close, {
        type: closeType,
      }, true)
    }
  }

  trackUnsubscribeReason = (reason, comment) => {
    snowplow.track('Unsubscribe reason', {
      reason,
      comment,
    }, false)
  }

  handleNeverMindClick = () => {
    this.trackCloseModal('never mind', 'never mind')
    this.close()
  }

  handleCloseModal = (type) => {
    const closeType = type === 'overlay' ? 'outside' : 'exit sign'

    this.trackCloseModal('close', closeType)
  }

  createReasonClickHandler = (reasonName, activeReasonNameLink) => () => {
    const { isUnsubscribeOfferTestAvailable, unsubscribeOfferTestAbValue } = this.props

    const isOfferTestFlow = (
      isUnsubscribeOfferTestAvailable
      && unsubscribeOfferTestAbValue !== 'a' // b, c
      && offerTestReasons.includes(reasonName)
    )

    if (isOfferTestFlow) {
      this.trackUnsubscribeReason(reasonName)
      actions.models.modals.open(constants.modals.cancelSubscription.offerTest25off, { reasonName, unsubscribeOfferTestAbValue })
      this.close()
    }
    else {
      activeReasonNameLink.set(reasonName)
    }
  }

  render() {
    const { submitted } = this.state
    const { name, reasons, userFullName } = this.props

    const activeReasonNameLink = ValueLink.state(this, 'activeReasonName')
    let reasonMessageLink = ValueLink.state(this, 'reasonMessage')

    if (submitted) {
      reasonMessageLink = reasonMessageLink.check((v) => v, messages.reasonMessageRequired)
    }

    return (
      <Modal
        name={name}
        title={{ ...messages.title,  values: { userName: userFullName } }}
        onClose={this.handleCloseModal}
      >
        <FormattedMessage tag="div" {...messages.text} />
        <div className={s.team}>
          <div className={s.avatars}>
            <img className={s.avatar} src={rachelPhoto} alt="Rachel" />
            <img className={s.avatar} src={sergeyPhoto} alt="Sergey" />
            <img className={s.avatar} src={andreyPhoto} alt="Andrey" />
            <img className={s.avatar} src={mariyaPhoto} alt="Mariya" />
          </div>
          <FormattedMessage className={s.teamLabel} tag="div" {...messages.teamLabel} />
        </div>
        <FormattedMessage className={s.reasonsTitle} tag="div" {...messages.reasonsTitle} />
        <div className={s.reasons}>
          {
            reasons.map(({ name, value: title }, index) => {
              const isActive = activeReasonNameLink.value === name

              const reasonClassName = cx(s.reason, {
                [s.active]: isActive,
              })

              return (
                <div key={name} className={reasonClassName}>
                  <div
                    className={s.reasonTitle}
                    onClick={this.createReasonClickHandler(name, activeReasonNameLink)}
                    data-testid={constants.testIds.resubscribe.reasonTitle}
                  >
                    <span>{index + 1}.</span>&nbsp;
                    {title}
                  </div>
                  {
                    isActive && (
                      <Input
                        className={s.reasonMessage}
                        placeholder={messages.reasonMessagePlaceholder}
                        valueLink={reasonMessageLink}
                        rows={3}
                        focusOnInit
                        multiline
                        data-testid={constants.testIds.resubscribe.reasonInput}
                      />
                    )
                  }
                </div>
              )
            })
          }
        </div>
        {
          Boolean(activeReasonNameLink.value) && (
            <Box alignCenter className={s.cancelSubscription}>
              <Button
                gray
                title={messages.cancelSubscription}
                onClick={this.handleCancelClick}
                dataTestId={constants.testIds.resubscribe.btnCancelSubscription}
              />
            </Box>
          )
        }
        <Box alignCenter className={s.neverMind}>
          <FormattedMessage {...messages.or} />&nbsp;
          <Link brandColor title={messages.neverMind} onClick={this.handleNeverMindClick} />
        </Box>
      </Modal>
    )
  }
}


export default compose(
  injectGlobals(({ getFeatureToggle, getAbValue }) => ({
    isUnsubscribeProlongedEnabled: getFeatureToggle(constants.features.unsubscribeProlonged),
    unsubscribeOfferTestAbValue: getAbValue(constants.abTests.unsubscribeOfferTest),
  })),
  storeData({
    reasons: () => actions.models.subscription.fetchUnsubscribeReasons(),
  }),
  connect({
    isProlongOfferVisible: (state, props) => {
      const {
        models: {
          subscription: { currPlan: { billingPeriod }, isPaused, isUnpaused, isMonthlyPlan },
          ui: { closedPopups },
        },
      } = state
      const { isUnsubscribeProlongedEnabled } = props

      const isMonthlyPay   = billingPeriod === 1
      const wasModalClosed = closedPopups.prolongOfferModal

      return (
        isUnsubscribeProlongedEnabled
        && isMonthlyPlan
        && isMonthlyPay
        && !wasModalClosed
        && !isPaused
        && !isUnpaused
      )
    },
    isUnsubscribeOfferTestAvailable: createSelector(
      (state) => state.models.subscription,
      (state) => state.models.ui.closedPopups.prolongOfferModal,
      checkIfUnsubscribeOfferTestAvailable
    ),
    userFullName: (state) => getUserFullNameFromState(state),
  })
)(OneLastQuestionModal)
